import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import {
  getRecipes, getRecipe, createRecipe, updateRecipe, deleteRecipe,
  getIngredients, createIngredient, downloadGyartmanylap, downloadLabel, downloadSzarmazasi,
  downloadReceptKarta, triggerPdfDownload, getSubscription, aiImportRecipe, printPdf, printRollLabel,
} from '../api/client'
import { Plus, ChevronRight, ChevronLeft, Clock, Pencil, FileText, Download, AlertTriangle, GripVertical, Lock, Trash2, Sparkles, X, Printer } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const SHELF_UNITS = [
  { value: 'ora', label: 'óra' },
  { value: 'nap', label: 'nap' },
  { value: 'het', label: 'hét' },
]

const NUTRITION_ROWS = [
  { key: 'energy_kj',      label: 'Energia (kJ)',                   bold: true },
  { key: 'energy_kcal',    label: 'Energia (kcal)',                  bold: true },
  { key: 'fat',            label: 'Zsír (g)',                        bold: true },
  { key: 'saturated_fat',  label: '  ebből telített zsírsavak (g)', bold: false },
  { key: 'carbs',          label: 'Szénhidrát (g)',                  bold: true },
  { key: 'sugars',         label: '  ebből cukrok (g)',              bold: false },
  { key: 'protein',        label: 'Fehérje (g)',                     bold: true },
  { key: 'fiber',          label: 'Rost (g)',                        bold: true },
  { key: 'salt',           label: 'Só (g)',                          bold: true },
]

function planLimitMessage(detail: string) {
  return detail.endsWith(':locked')
    ? 'Ez a recept zárolva van a csomagod receptlimitje miatt — előfizess (vagy válts magasabb csomagra) a hozzáféréshez.'
    : 'Elérte a receptek számára vonatkozó korlátot!'
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

function SortableRow({ id, children }: { id: string; children: (handleProps: any) => React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }
  return (
    <div ref={setNodeRef} style={style}>
      {children({ ...attributes, ...listeners })}
    </div>
  )
}

function IngredientPicker({ ingredientId, componentRecipeId, onChange, ingredients, recipes }: {
  ingredientId: number
  componentRecipeId: number
  onChange: (sel: { ingredient_id?: number; component_recipe_id?: number }, name: string) => void
  ingredients: any[]
  recipes: any[]
}) {
  const selectedIng = ingredients.find((i: any) => i.id === ingredientId)
  const selectedRecipe = recipes.find((r: any) => r.id === componentRecipeId)
  const selectedName = selectedIng?.name || selectedRecipe?.name || ''
  const [query, setQuery] = useState(selectedName)
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()

  useEffect(() => {
    setQuery(selectedName)
  }, [ingredientId, componentRecipeId, selectedName])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const combined = [
    ...recipes.map((r: any) => ({ kind: 'recipe' as const, id: r.id, name: r.name })),
    ...ingredients.map((i: any) => ({ kind: 'ing' as const, id: i.id, name: i.name })),
  ]
  const filtered = query.length === 0
    ? combined.slice(0, 50)
    : combined.filter(o => o.name.toLowerCase().includes(query.toLowerCase())).slice(0, 50)

  const trimmedQuery = query.trim()
  const exactMatch = ingredients.some((i: any) => i.name.toLowerCase() === trimmedQuery.toLowerCase())
  const showCreateOption = trimmedQuery.length > 1 && !exactMatch

  async function createNew() {
    setCreating(true)
    try {
      const res = await createIngredient({ name: trimmedQuery })
      qc.invalidateQueries({ queryKey: ['ingredients'] })
      onChange({ ingredient_id: res.data.id }, res.data.name)
      setQuery(res.data.name)
      setOpen(false)
      toast.success(`"${res.data.name}" alapanyag létrehozva — tápértékeit az Alapanyagok oldalon add meg`)
    } catch {
      toast.error('Nem sikerült létrehozni az alapanyagot')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div ref={ref} className="relative flex-1">
      <input
        className="input w-full text-sm"
        placeholder="Alapanyag vagy alaprecept keresése..."
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-0.5 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.length === 0 && !showCreateOption && (
            <div className="px-3 py-2 text-xs text-gray-400">Nincs találat</div>
          )}
          {filtered.map((o) => {
            const isSelected = o.kind === 'recipe' ? o.id === componentRecipeId : o.id === ingredientId
            return (
              <button
                key={`${o.kind}-${o.id}`}
                type="button"
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-brand-50 flex items-center justify-between gap-2 ${isSelected ? 'bg-brand-50 font-medium text-brand-700' : 'text-gray-800'}`}
                onMouseDown={e => {
                  e.preventDefault()
                  onChange(o.kind === 'recipe' ? { component_recipe_id: o.id } : { ingredient_id: o.id }, o.name)
                  setQuery(o.name)
                  setOpen(false)
                }}
              >
                <span>{o.name}</span>
                {o.kind === 'recipe' && (
                  <span className="text-[10px] uppercase tracking-wide text-brand-500 bg-brand-50 border border-brand-200 rounded px-1 py-0.5 shrink-0">alaprecept</span>
                )}
              </button>
            )
          })}
          {showCreateOption && (
            <button
              type="button"
              className="w-full text-left px-3 py-1.5 text-sm text-brand-700 font-medium hover:bg-brand-50 border-t border-gray-100 flex items-center gap-1.5 disabled:opacity-60"
              onMouseDown={e => { e.preventDefault(); createNew() }}
              disabled={creating}
            >
              <Plus size={12} /> {creating ? 'Létrehozás...' : `Új alapanyag: "${trimmedQuery}"`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function AiImportModal({ active, onClose, onImported }: {
  active: boolean
  onClose: () => void
  onImported: (draft: any) => void
}) {
  const [text, setText] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)

  async function submit() {
    if (!text.trim() && !file) {
      toast.error('Adj meg szöveget vagy tölts fel egy fájlt')
      return
    }
    setLoading(true)
    try {
      const res = await aiImportRecipe({ text: text.trim() || undefined, file: file || undefined })
      const data = res.data
      onImported({
        name: data.name,
        batch_yield_grams: data.batch_yield_grams || undefined,
        yield_unit: data.yield_unit || 'db',
        instructions: data.instructions || undefined,
        ingredients: (data.ingredients || []).map((i: any) => ({
          ingredient_id: i.matched_ingredient_id || undefined,
          quantity: i.quantity,
          quantity_grams: i.quantity,
          unit: i.unit || 'g',
          pending_name: i.matched_ingredient_id ? undefined : i.raw_name,
        })),
      })
      toast.success('Recept feldolgozva — nézd át és mentsd el')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Hiba a feldolgozás közben')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800 flex items-center gap-1.5">
            <Sparkles size={16} className="text-brand-600" /> AI recept feltöltés
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        {!active ? (
          <div className="text-sm text-gray-600 space-y-3">
            <p>Ez egy külön fizetős funkció — a receptedet (bemásolt szöveg vagy Word/Excel fájl) az AI automatikusan feldolgozza és kitölti a recept-űrlapot helyetted, te csak ellenőrzöd és mented.</p>
            <p>Egyedi árajánlatért vedd fel velünk a kapcsolatot emailben: <a href="mailto:info@cukraszdoc.hu" className="text-brand-600 font-medium">info@cukraszdoc.hu</a></p>
          </div>
        ) : (
          <div className="space-y-3">
            <textarea className="input h-32 text-sm" placeholder="Illeszd be ide a recept szövegét..."
              value={text} onChange={e => setText(e.target.value)} />
            <div className="text-xs text-gray-400 text-center">— vagy —</div>
            <input type="file" accept=".docx,.xlsx,.xlsm,.txt" className="input text-sm"
              onChange={e => setFile(e.target.files?.[0] || null)} />
            <button type="button" className="btn-primary w-full" onClick={submit} disabled={loading}>
              {loading ? 'Feldolgozás...' : 'Feldolgozás AI-val'}
            </button>
            <p className="text-xs text-gray-400">A feldolgozás után a normál recept-űrlapon átnézheted és javíthatod, mielőtt mentenéd.</p>
          </div>
        )}
      </div>
    </div>
  )
}

function RecipeForm({
  initial, onSave, onCancel, ingredients, recipes = [],
}: {
  initial?: any
  onSave: (data: any) => void
  onCancel: () => void
  ingredients: any[]
  recipes?: any[]
}) {
  let _rowId = 0
  const newId = () => `row-${++_rowId}-${Math.random().toString(36).slice(2, 6)}`
  const availableRecipes = recipes.filter((r: any) => r.id !== initial?.id)

  const buildRows = (ings: any[]) => {
    if (!ings?.length) return [{ id: newId(), type: 'ing', ingredient_id: 0, component_recipe_id: 0, quantity: '', unit: 'g', notes: '' }]
    const result: any[] = []
    let lastSection = ''
    for (const i of ings) {
      const sec = i.section || ''
      if (sec !== lastSection) {
        if (sec) result.push({ id: newId(), type: 'section', name: sec })
        lastSection = sec
      }
      result.push({ id: newId(), type: 'ing', ingredient_id: i.ingredient_id || 0, component_recipe_id: i.component_recipe_id || 0, quantity: i.quantity ?? i.quantity_grams, unit: i.unit || 'g', notes: i.notes || '', pendingName: i.pending_name || '' })
    }
    return result
  }
  const [ingRows, setIngRows] = useState<any[]>(() => buildRows(initial?.ingredients))
  const [yieldUnit, setYieldUnit] = useState<string>(initial?.yield_unit || 'db')
  const [notFreezable, setNotFreezable] = useState<boolean>(initial ? !initial?.frozen_shelf_life_months : false)

  const parseBaking = (s?: string | null) => {
    const m = /(-?\d+(?:[.,]\d+)?)\s*°?\s*C?\s*\/\s*(\d+(?:[.,]\d+)?)\s*perc/i.exec(s || '')
    if (m) return { temp: m[1].replace(',', '.'), time: m[2].replace(',', '.') }
    return { temp: '', time: '' }
  }
  const bakingInit = parseBaking(initial?.baking_temp)
  const bakingLegacyFallback = initial?.baking_temp && !bakingInit.temp && !bakingInit.time ? initial.baking_temp : ''

  const TO_G: Record<string, number> = { g: 1, dkg: 10, kg: 1000 }

  const autoWeightG = ingRows
    .filter(r => r.type === 'ing' && (r.ingredient_id > 0 || r.component_recipe_id > 0) && Number(r.quantity) > 0)
    .reduce((sum, r) => {
      const factor = TO_G[(r.unit || 'g').toLowerCase()] ?? null
      return factor != null ? sum + Number(r.quantity) * factor : sum
    }, 0)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setIngRows(rows => {
        const oldIdx = rows.findIndex(r => r.id === active.id)
        const newIdx = rows.findIndex(r => r.id === over.id)
        return arrayMove(rows, oldIdx, newIdx)
      })
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const incomplete = ingRows.some(r => r.type === 'ing' && !(r.ingredient_id > 0 || r.component_recipe_id > 0) && Number(r.quantity) > 0)
    if (incomplete) {
      toast.error('Van olyan alapanyag sor, ahol nincs kiválasztva alapanyag vagy alaprecept a listából — válaszd ki, vagy hozd létre a keresőben, különben nem kerül be a receptbe.')
      return
    }
    const fd = new FormData(e.currentTarget)
    const shelfVal = Number(fd.get('shelf_life_value'))
    const shelfUnit = fd.get('shelf_life_unit') as string
    let shelfDays: number = 3
    if (shelfVal > 0) {
      if (shelfUnit === 'ora') shelfDays = Math.max(1, Math.round(shelfVal / 24))
      else if (shelfUnit === 'nap') shelfDays = shelfVal
      else if (shelfUnit === 'het') shelfDays = shelfVal * 7
    }
    const bakingTempVal = String(fd.get('baking_temp_value') || '').trim()
    const bakingTimeVal = String(fd.get('baking_time_value') || '').trim()
    let bakingTemp: string | undefined
    if (bakingTempVal || bakingTimeVal) {
      bakingTemp = [bakingTempVal ? `${bakingTempVal}°C` : '', bakingTimeVal ? `${bakingTimeVal} perc` : '']
        .filter(Boolean).join(' / ')
    } else {
      bakingTemp = String(fd.get('baking_temp_legacy') || '').trim() || undefined
    }
    const servingCount = fd.get('serving_count') ? Number(fd.get('serving_count')) : undefined
    const servingWeightG = fd.get('serving_weight_g') ? Number(fd.get('serving_weight_g')) : undefined
    const yieldUnitVal = String(fd.get('yield_unit') || 'db')
    const batchG = yieldUnitVal === 'g'
      ? (Number(fd.get('batch_yield_grams')) || autoWeightG || 500)
      : (autoWeightG || 500)

    onSave({
      name: fd.get('name'),
      yield_unit: yieldUnitVal,
      batch_yield_grams: batchG,
      serving_count: servingCount,
      serving_weight_g: servingWeightG,
      instructions: fd.get('instructions') || undefined,
      shelf_life_days: shelfDays,
      storage_temp: fd.get('storage_temp') || undefined,
      storage_temp_min: 2,
      storage_temp_max: 8,
      preservation_method: fd.get('preservation_method') || undefined,
      storage_notes: fd.get('storage_notes') || undefined,
      baking_temp: bakingTemp,
      frozen_shelf_life_months: notFreezable ? undefined : (fd.get('frozen_shelf_life_months') ? Number(fd.get('frozen_shelf_life_months')) : undefined),
      frozen_storage_temp: notFreezable ? undefined : (fd.get('frozen_storage_temp') || undefined),
      approved_by: fd.get('approved_by') || undefined,
      ingredients: (() => {
        let currentSection = ''
        let order = 0
        const result: any[] = []
        for (const r of ingRows) {
          if (r.type === 'section') { currentSection = r.name; continue }
          if (r.ingredient_id > 0 || r.component_recipe_id > 0) {
            const qty = Number(r.quantity)
            const u = r.unit || 'g'
            const factor = TO_G[u.toLowerCase()] ?? 1
            result.push({
              ingredient_id: r.ingredient_id > 0 ? r.ingredient_id : undefined,
              component_recipe_id: r.component_recipe_id > 0 ? r.component_recipe_id : undefined,
              quantity: qty,
              unit: u,
              quantity_grams: ['g', 'dkg', 'kg'].includes(u.toLowerCase()) ? qty * factor : qty,
              section: currentSection || undefined,
              sort_order: order++,
              notes: r.notes || undefined,
              order_index: order - 1,
            })
          }
        }
        return result
      })(),
    })
  }

  const shelfDays = initial?.shelf_life_days || 3
  let shelfVal: number = shelfDays
  let shelfUnit = 'nap'
  if (shelfDays % 7 === 0 && shelfDays >= 7) { shelfVal = shelfDays / 7; shelfUnit = 'het' }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label">Recept neve *</label>
          <input name="name" className="input" required defaultValue={initial?.name} />
        </div>
        <div className="col-span-2">
          <label className="label">1 adag hozama <span className="text-gray-400 font-normal text-xs">— termelési alapértelmezett</span></label>
          <div className="flex gap-3 mb-2">
            {([
              { v: 'db', label: 'Darab (db)' },
              { v: 'szelet', label: 'Szelet' },
              { v: 'g', label: 'Súly (g)' },
            ] as const).map(({ v, label }) => (
              <label key={v} className="cursor-pointer flex items-center gap-1.5">
                <input type="radio" name="yield_unit" value={v} checked={yieldUnit === v}
                  onChange={() => setYieldUnit(v)} className="accent-brand-500" />
                <span className="text-sm font-medium">{label}</span>
              </label>
            ))}
          </div>
          {yieldUnit === 'g' ? (
            <div>
              <input name="batch_yield_grams" type="number" className="input max-w-xs" min={1} step="any"
                defaultValue={initial?.yield_unit === 'g' ? (initial?.batch_yield_grams || '') : ''}
                placeholder={autoWeightG > 0 ? `${autoWeightG} g (automatikus)` : 'pl. 1500'} />
              {autoWeightG > 0 && (
                <p className="text-xs text-brand-600 mt-1">
                  Összetevők összesúlya: <strong>{autoWeightG} g</strong> — üresen hagyva ezt használja a termelési napló. Töltsd ki, ha sütési veszteséggel számolsz.
                </p>
              )}
            </div>
          ) : (
            <div className="flex gap-3 items-end">
              <div>
                <label className="label text-xs text-gray-400">Darab száma</label>
                <input name="serving_count" type="number" className="input w-28" min={1} step={1}
                  defaultValue={initial?.serving_count || ''}
                  placeholder={yieldUnit === 'szelet' ? 'pl. 12' : 'pl. 30'} />
              </div>
              <div>
                <label className="label text-xs text-gray-400">1 db súlya (g)</label>
                <input name="serving_weight_g" type="number" className="input w-28" min={1} step={1}
                  defaultValue={initial?.serving_weight_g || ''} placeholder="pl. 60" />
              </div>
            </div>
          )}
        </div>
        <div>
          <label className="label">Fogyasztható</label>
          <div className="flex gap-2">
            <input name="shelf_life_value" type="number" className="input w-20"
              min={0} step={1} defaultValue={shelfVal} />
            <select name="shelf_life_unit" className="input w-20" defaultValue={shelfUnit}>
              {SHELF_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="label">Tárolási hőmérséklet</label>
          <div className="flex items-center gap-1">
            <input name="storage_temp" className="input" placeholder="pl. 0–6"
              defaultValue={(initial?.storage_temp || '').replace('°C', '')} />
            <span className="text-sm text-gray-500 shrink-0">°C</span>
          </div>
        </div>
      </div>

      <div>
        <label className="flex items-center gap-2 cursor-pointer select-none mb-2">
          <input type="checkbox" className="accent-brand-600" checked={notFreezable}
            onChange={e => setNotFreezable(e.target.checked)} />
          <span className="text-sm font-medium text-gray-700">Nem fagyasztható (pl. fagylalt, vagy nincs szükség fagyasztási adatra)</span>
        </label>
        {!notFreezable && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Fagyasztva megőrzi (hónap)</label>
              <input name="frozen_shelf_life_months" type="number" className="input"
                min={1} step={1} placeholder="pl. 3"
                defaultValue={initial?.frozen_shelf_life_months ?? ''} />
            </div>
            <div>
              <label className="label">Fagyasztási hőmérséklet</label>
              <div className="flex items-center gap-1">
                <input name="frozen_storage_temp" className="input" placeholder="-18"
                  defaultValue={(initial?.frozen_storage_temp || '-18').replace('°C', '')} />
                <span className="text-sm text-gray-500 shrink-0">°C</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="label">Sütési hőfok / idő <span className="text-gray-400 font-normal text-xs">— hagyd üresen, ha nem releváns (pl. fagylalt)</span></label>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <input name="baking_temp_value" type="number" className="input w-24" step="any"
              placeholder="180" defaultValue={bakingInit.temp} />
            <span className="text-sm text-gray-500 shrink-0">°C</span>
          </div>
          <div className="flex items-center gap-1">
            <input name="baking_time_value" type="number" className="input w-24" step="any" min={0}
              placeholder="20" defaultValue={bakingInit.time} />
            <span className="text-sm text-gray-500 shrink-0">perc</span>
          </div>
        </div>
        {bakingLegacyFallback && (
          <input type="hidden" name="baking_temp_legacy" value={bakingLegacyFallback} />
        )}
      </div>
      <div>
        <label className="label">Tartósítási eljárás</label>
        <input name="preservation_method" className="input"
          placeholder="pl. Hőkezelés sütéssel, gyorsfagyasztással történő hűtés"
          defaultValue={initial?.preservation_method || ''} />
      </div>
      <div>
        <label className="label">Forgalmazás módja / Csomagolás / Tárolás / Szállítás</label>
        <textarea name="storage_notes" className="input h-16"
          placeholder="pl. Hűtve tárolandó, tortadobozban szállítandó. Kiskereskedelmi forgalomba nem kerül."
          defaultValue={initial?.storage_notes || ''} />
      </div>
      <div>
        <label className="label">Jóváhagyta</label>
        <input name="approved_by" className="input" defaultValue={initial?.approved_by || ''} />
      </div>
      <div>
        <label className="label">Leírás / utasítások</label>
        <textarea name="instructions" className="input h-20" defaultValue={initial?.instructions || ''} />
      </div>

      <div>
        <label className="label">Alapanyagok</label>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ingRows.map(r => r.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1 mb-2">
              {ingRows.map((row, idx) => (
                <SortableRow key={row.id} id={row.id}>
                  {(handleProps) => row.type === 'section' ? (
                    <div className="flex items-center gap-1 mt-3 mb-1">
                      <button type="button" className="cursor-grab text-gray-300 hover:text-gray-500 px-0.5 touch-none" {...handleProps}>
                        <GripVertical size={14} />
                      </button>
                      <input
                        className="input flex-1 text-sm font-semibold text-brand-700 bg-brand-50 border-brand-200 py-1"
                        value={row.name}
                        placeholder="Szekció neve (pl. Tészta)"
                        onChange={e => { const u = [...ingRows]; u[idx] = { ...u[idx], name: e.target.value }; setIngRows(u) }}
                      />
                      <button type="button" className="text-red-400 hover:text-red-600 px-1"
                        onClick={() => setIngRows(ingRows.filter((_, i) => i !== idx))}>✕</button>
                    </div>
                  ) : (
                    <div className="pl-3 border-l-2 border-gray-100">
                    <div className="flex gap-1 items-center">
                      <button type="button" className="cursor-grab text-gray-300 hover:text-gray-500 px-0.5 touch-none" {...handleProps}>
                        <GripVertical size={14} />
                      </button>
                      <IngredientPicker
                        ingredientId={row.ingredient_id}
                        componentRecipeId={row.component_recipe_id}
                        ingredients={ingredients}
                        recipes={availableRecipes}
                        onChange={(sel) => { const u = [...ingRows]; u[idx] = { ...u[idx], ingredient_id: sel.ingredient_id || 0, component_recipe_id: sel.component_recipe_id || 0, pendingName: '' }; setIngRows(u) }}
                      />
                      <input type="number" placeholder="Menny." className="input w-24 text-sm"
                        value={row.quantity}
                        onChange={e => { const u = [...ingRows]; u[idx] = { ...u[idx], quantity: e.target.value }; setIngRows(u) }}
                        min={0} step={0.1} />
                      <select className="input w-16 text-sm" value={row.unit}
                        onChange={e => { const u = [...ingRows]; u[idx] = { ...u[idx], unit: e.target.value }; setIngRows(u) }}>
                        <option>g</option>
                        <option>dkg</option>
                        <option>kg</option>
                        <option>ml</option>
                        <option>l</option>
                        <option>db</option>
                      </select>
                      <button type="button" className="text-red-400 hover:text-red-600 px-1"
                        onClick={() => setIngRows(ingRows.filter((_, i) => i !== idx))}>✕</button>
                    </div>
                    {row.pendingName && !row.ingredient_id && !row.component_recipe_id && (
                      <p className="text-xs text-amber-600 mt-0.5 ml-5">AI javaslat: „{row.pendingName}" — válaszd ki vagy hozd létre a keresőben</p>
                    )}
                    </div>
                  )}
                </SortableRow>
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <div className="flex gap-2 flex-wrap">
          <button type="button" className="btn-secondary text-sm"
            onClick={() => setIngRows([...ingRows, { id: newId(), type: 'ing', ingredient_id: 0, component_recipe_id: 0, quantity: '', unit: 'g', notes: '' }])}>
            + Alapanyag
          </button>
          <button type="button" className="text-sm px-3 py-1.5 border border-brand-300 text-brand-700 rounded-lg hover:bg-brand-50 transition-colors"
            onClick={() => setIngRows([...ingRows, { id: newId(), type: 'section', name: '' }])}>
            + Szekció (pl. Tészta / Töltelék)
          </button>
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <button type="button" className="btn-secondary" onClick={onCancel}>Mégse</button>
        <button type="submit" className="btn-primary">Mentés</button>
      </div>
    </form>
  )
}

function NutritionTable({ nutrition, servingCount, servingWeightG, totalWeightG }: {
  nutrition: any, servingCount?: number, servingWeightG?: number, totalWeightG?: number
}) {
  const servingG = servingWeightG
    ? servingWeightG
    : (servingCount && totalWeightG ? totalWeightG / servingCount : null)

  return (
    <div className="mt-4">
      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Tápérték (100 g-ra)</div>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-brand-600 text-white">
            <th className="text-left px-2 py-1.5 font-medium">Tápanyag</th>
            <th className="text-right px-2 py-1.5 font-medium">100 g</th>
            {servingG && <th className="text-right px-2 py-1.5 font-medium">1 szelet ({servingG.toFixed(0)} g)</th>}
          </tr>
        </thead>
        <tbody>
          {NUTRITION_ROWS.map((row, i) => {
            const val = nutrition[row.key]
            const servingVal = servingG && val != null ? (val * servingG / 100) : null
            return (
              <tr key={row.key} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className={`px-2 py-1 ${row.bold ? 'font-semibold text-gray-800' : 'text-gray-600 pl-4'}`}>
                  {row.label}
                </td>
                <td className={`text-right px-2 py-1 ${row.bold ? 'font-semibold' : ''}`}>
                  {val != null ? Number(val).toFixed(2).replace('.', ',') : '—'}
                </td>
                {servingG && (
                  <td className="text-right px-2 py-1 text-gray-600">
                    {servingVal != null ? servingVal.toFixed(2).replace('.', ',') : '—'}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
      {nutrition.ingredient_list && (
        <p className="text-xs text-gray-400 mt-1">Összetevők: {nutrition.ingredient_list}</p>
      )}
    </div>
  )
}

function AllergenWarnings({ warnings }: { warnings: string[] }) {
  if (!warnings?.length) return null
  return (
    <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex gap-2">
      <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
      <div>
        <div className="text-xs font-semibold text-amber-700 mb-0.5">Allergének az alapanyagokban:</div>
        <div className="text-xs text-amber-800">{warnings.join(', ')}</div>
      </div>
    </div>
  )
}

function PdfButtons({ recipe }: { recipe: any }) {
  if (recipe.locked) {
    return (
      <div className="flex items-start gap-2.5 p-4 border border-amber-200 rounded-lg bg-amber-50 text-amber-800">
        <Lock size={16} className="shrink-0 mt-0.5" />
        <div className="text-sm">
          Ez a recept zárolva van a csomagod receptlimitje miatt, ezért dokumentum nem generálható hozzá.
          Fizess elő (vagy válts magasabb csomagra) a hozzáféréshez.
        </div>
      </div>
    )
  }
  const today = new Date().toISOString().slice(0, 10)
  const addDays = (d: string, n: number) => {
    const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10)
  }
  const [prodDate, setProdDate] = useState(today)
  const [expDate, setExpDate] = useState(() => addDays(today, recipe.shelf_life_days || 3))
  const [showSzarmazasiForm, setShowSzarmazasiForm] = useState(false)
  const [showLabelForm, setShowLabelForm] = useState(false)
  const [labelProdDate, setLabelProdDate] = useState(today)
  const [labelExpiryDate, setLabelExpiryDate] = useState(() => addDays(today, recipe.shelf_life_days || 3))
  const [labelMode, setLabelMode] = useState<'fogyasztható' | 'fagyasztva' | 'csak_lejarat'>('fogyasztható')
  const [includeQr, setIncludeQr] = useState(false)
  const [loadingKey, setLoadingKey] = useState<string | null>(null)

  const pdfErrorToast = async (err: any) => {
    // responseType: 'blob' esetén a hiba body-ja is Blob, nem parsed JSON — ki kell csomagolni
    let detail = err?.response?.data?.detail || ''
    const data = err?.response?.data
    if (data instanceof Blob) {
      try { detail = JSON.parse(await data.text())?.detail || '' } catch { /* nem JSON hiba */ }
    }
    if (typeof detail === 'string' && detail.startsWith('PLAN_LIMIT:')) toast.error(planLimitMessage(detail))
    else toast.error('PDF generálás sikertelen')
  }

  const doDownload = async (key: string, fn: () => Promise<any>, filename: string) => {
    setLoadingKey(key)
    try {
      const res = await fn()
      downloadBlob(res.data, filename)
    } catch (err) {
      await pdfErrorToast(err)
    } finally {
      setLoadingKey(null)
    }
  }

  const doPrint = async (key: string, fn: () => Promise<any>, roll = false) => {
    setLoadingKey(key)
    try {
      const res = await fn()
      if (roll) {                                 // Brother 62 mm: a címkenyomtató segéden át
        if (await printRollLabel(res.data) === 'cloud') toast.success('Elküldve a címkenyomtatóra')
      }
      else printPdf(res.data)
    } catch (err: any) {
      if (!err?.response) { toast.error(err?.message || 'Nyomtatási hiba'); return }
      await pdfErrorToast(err)
    } finally {
      setLoadingKey(null)
    }
  }

  const LABEL_SIZES = [
    { value: '100x70', label: '100×70 mm' },
    { value: '100x60', label: '100×60 mm' },
    { value: '60x50', label: '60×50 mm' },
    { value: '60x40', label: '60×40 mm' },
    { value: '50x30', label: '50×30 mm' },
    { value: 'A4', label: 'A4 (9×)' },
    { value: '62_kicsi', label: 'Brother 62 mm – kicsi' },
    { value: '62_tekercs', label: 'Brother 62 mm – nagy' },
  ]

  return (
    <div className="mt-4 space-y-3">
      {/* Recept kártya — egy kattintás, konyhai nyomtatáshoz */}
      <div className="flex items-center justify-between p-3 border border-green-200 rounded-lg bg-green-50">
        <div className="flex items-center gap-2.5">
          <FileText size={15} className="text-green-700 shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-800">Recept kártya</div>
            <div className="text-xs text-gray-400">Konyhai nyomtatáshoz — alapanyagok, sütési hőfok, utasítások</div>
          </div>
        </div>
        <div className="flex rounded-lg overflow-hidden shrink-0">
          <button className="text-sm flex items-center gap-1.5 px-3 py-2 rounded-r-none font-semibold transition-colors"
            style={{ background: '#4E813F', color: '#fff' }}
            onClick={() => doDownload('recept', () => downloadReceptKarta(recipe.id), `recept_${recipe.name}.pdf`)}
            disabled={loadingKey === 'recept'}
            title="Letöltés">
            <Download size={13} /> {loadingKey === 'recept' ? 'Generálás...' : 'Letöltés'}
          </button>
          <button className="px-2.5 py-2 border-l border-white/30 transition-colors"
            style={{ background: '#4E813F', color: '#fff' }}
            onClick={() => doPrint('recept_print', () => downloadReceptKarta(recipe.id))}
            disabled={loadingKey === 'recept_print'}
            title="Nyomtatás">
            <Printer size={13} />
          </button>
        </div>
      </div>

      {/* Gyártmánylap — egy kattintás */}
      <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center gap-2.5">
          <FileText size={15} className="text-brand-600 shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-800">Gyártmánylap</div>
            <div className="text-xs text-gray-400">Termékspecifikáció — partnereknek, viszonteladóknak</div>
          </div>
        </div>
        <div className="flex rounded-lg overflow-hidden shrink-0">
          <button className="btn-primary text-sm flex items-center gap-1.5 rounded-r-none"
            onClick={() => doDownload('gyartmanylap', () => downloadGyartmanylap(recipe.id), `gyartmanylap_${recipe.name}.pdf`)}
            disabled={loadingKey === 'gyartmanylap'}
            title="Letöltés">
            <Download size={13} /> {loadingKey === 'gyartmanylap' ? 'Generálás...' : 'Letöltés'}
          </button>
          <button className="btn-primary text-sm rounded-l-none border-l border-white/30 px-2.5"
            onClick={() => doPrint('gyartmanylap_print', () => downloadGyartmanylap(recipe.id))}
            disabled={loadingKey === 'gyartmanylap_print'}
            title="Nyomtatás">
            <Printer size={13} />
          </button>
        </div>
      </div>

      {/* Származási igazolás — dátumok kellenek */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-3 bg-gray-50">
          <div className="flex items-center gap-2.5">
            <FileText size={15} className="text-brand-600 shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-800">Származási igazolás</div>
              <div className="text-xs text-gray-400">Kísérőirat — óvoda, iskola, rendezvény</div>
            </div>
          </div>
          <button className="btn-primary text-sm flex items-center gap-1.5 shrink-0"
            onClick={() => setShowSzarmazasiForm(v => !v)}>
            <Download size={13} /> {showSzarmazasiForm ? 'Bezárás' : 'Letöltés'}
          </button>
        </div>
        {showSzarmazasiForm && (
          <div className="border-t border-gray-100 px-3 py-3 bg-white space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label text-xs">Készítés dátuma</label>
                <input type="date" className="input text-sm" value={prodDate} onChange={e => setProdDate(e.target.value)} />
              </div>
              <div>
                <label className="label text-xs">Fogyasztható</label>
                <input type="date" className="input text-sm" value={expDate} onChange={e => setExpDate(e.target.value)} />
              </div>
            </div>
            <button className="btn-primary text-sm flex items-center gap-1.5"
              onClick={() => doDownload('haccp', () => downloadSzarmazasi(recipe.id, { production_date: prodDate, expiry_date: expDate }), `szarmazasi_${recipe.name}.pdf`)}
              disabled={loadingKey === 'haccp'}>
              <Download size={13} /> {loadingKey === 'haccp' ? 'Generálás...' : 'PDF letöltés'}
            </button>
          </div>
        )}
      </div>

      {/* Etikett — dátumok megadása után méretenként */}
      <div className="border border-brand-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-3 bg-brand-50/40">
          <div className="flex items-center gap-2.5">
            <FileText size={15} className="text-brand-600 shrink-0" />
            <div>
              <div className="text-sm font-medium text-gray-800">Etikett címke</div>
              <div className="text-xs text-gray-400">Dátumok megadása után méretenként tölthető le</div>
            </div>
          </div>
          <button className="btn-primary text-sm flex items-center gap-1.5 shrink-0"
            onClick={() => setShowLabelForm(v => !v)}>
            <Download size={13} /> {showLabelForm ? 'Bezárás' : 'Letöltés'}
          </button>
        </div>
        {showLabelForm && (
          <div className="border-t border-brand-100 px-3 py-3 bg-white space-y-3">
            <div className="space-y-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="radio" name={`lmode_${recipe.id}`} className="accent-brand-600" checked={labelMode === 'fogyasztható'} onChange={() => setLabelMode('fogyasztható')} />
                <span className="text-sm text-gray-700">
                  Fogyasztható: gyártástól számított {recipe.shelf_life_days || '?'} nap · {recipe.storage_temp || `${recipe.storage_temp_min ?? 0}–${recipe.storage_temp_max ?? 6}°C`}
                </span>
              </label>
              {!!recipe.frozen_shelf_life_months && (
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="radio" name={`lmode_${recipe.id}`} className="accent-brand-600" checked={labelMode === 'fagyasztva'} onChange={() => setLabelMode('fagyasztva')} />
                  <span className="text-sm text-gray-700">
                    Minőségét megőrzi: gyártástól számított {recipe.frozen_shelf_life_months} hónap · {recipe.frozen_storage_temp || '-18'}°C
                  </span>
                </label>
              )}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="radio" name={`lmode_${recipe.id}`} className="accent-brand-600" checked={labelMode === 'csak_lejarat'} onChange={() => setLabelMode('csak_lejarat')} />
                <span className="text-sm text-gray-700">
                  Csak lejárat dátuma <span className="text-xs text-gray-400">(NÉBIH kérés — gyártás dátuma nem jelenik meg)</span>
                </span>
              </label>
            </div>
            {labelMode !== 'csak_lejarat' ? (
              <div>
                <label className="label text-xs">Gyártva (üresen hagyható)</label>
                <input type="date" className="input text-sm" value={labelProdDate} onChange={e => setLabelProdDate(e.target.value)} />
                <p className="text-xs text-gray-400 mt-1">Ha üresen hagyod a dátumot, a cimkén üres marad — kézzel kitölthető.</p>
              </div>
            ) : (
              <div>
                <label className="label text-xs">Fogyasztható (dátum) *</label>
                <input type="date" className="input text-sm" value={labelExpiryDate} onChange={e => setLabelExpiryDate(e.target.value)} />
                <p className="text-xs text-gray-400 mt-1">Ez a konkrét dátum jelenik meg a Fogyasztható: sor után.</p>
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" className="accent-brand-600" checked={includeQr} onChange={e => setIncludeQr(e.target.checked)} />
              <span className="text-sm text-gray-700">
                Nyomonkövetési QR-kód a címke sarkába (beszállítói tétel adatok)
              </span>
            </label>
            <div className="flex gap-2 flex-wrap">
              {LABEL_SIZES.map(s => {
                const buildParams = () => (labelMode === 'csak_lejarat' ? {
                  recipe_id: recipe.id,
                  label_size: s.value,
                  production_date: null,
                  best_before_date: labelExpiryDate || null,
                  label_type: labelMode,
                  include_qr: includeQr,
                } : {
                  recipe_id: recipe.id,
                  label_size: s.value,
                  production_date: labelProdDate || null,
                  label_type: labelMode,
                  include_qr: includeQr,
                })
                return (
                  <div key={s.value} className="flex rounded-lg overflow-hidden">
                    <button
                      className="btn-primary text-sm flex items-center gap-1.5 rounded-r-none"
                      disabled={loadingKey === `label_${s.value}`}
                      onClick={() => doDownload(
                        `label_${s.value}`,
                        () => downloadLabel(recipe.id, buildParams()),
                        `etikett_${s.value}_${recipe.name}.pdf`
                      )}
                      title="Letöltés">
                      <Download size={13} />
                      {loadingKey === `label_${s.value}` ? '...' : s.label}
                    </button>
                    <button
                      className="btn-primary text-sm rounded-l-none border-l border-white/30 px-2.5"
                      disabled={loadingKey === `label_print_${s.value}`}
                      onClick={() => doPrint(`label_print_${s.value}`, () => downloadLabel(recipe.id, buildParams()), s.value.startsWith('62_'))}
                      title="Nyomtatás">
                      <Printer size={13} />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function RecipesPage() {
  const qc = useQueryClient()
  const location = useLocation()
  const [showForm, setShowForm] = useState(false)
  const [editingRecipe, setEditingRecipe] = useState<any>(null)
  const [selected, setSelected] = useState<any>(null)
  const [detailTab, setDetailTab] = useState<'recept' | 'dokumentumok'>('recept')
  const [recipeSearch, setRecipeSearch] = useState('')
  const [showAiImport, setShowAiImport] = useState(false)
  const [aiDraft, setAiDraft] = useState<any>(null)

  useEffect(() => {
    setSelected(null)
    setShowForm(false)
    setEditingRecipe(null)
  }, [location.key])

  const { data: recipes = [] } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => getRecipes().then(r => r.data),
  })

  const { data: selectedRecipe, isLoading: recipeLoading } = useQuery({
    queryKey: ['recipe', selected?.id],
    queryFn: () => getRecipe(selected.id).then(r => r.data),
    enabled: !!selected,
  })

  const { data: ingredients = [] } = useQuery({
    queryKey: ['ingredients'],
    queryFn: () => getIngredients().then(r => r.data),
  })

  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => getSubscription().then(r => r.data),
  })

  const atLimit = sub && !sub.can_create

  const createMutation = useMutation({
    mutationFn: createRecipe,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['recipes'] })
      qc.invalidateQueries({ queryKey: ['subscription'] })
      setShowForm(false)
      setAiDraft(null)
      setSelected({ id: res.data.id })
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail || ''
      if (detail.startsWith('PLAN_LIMIT:')) toast.error(planLimitMessage(detail))
      else if (err?.response?.status === 409) toast.error('Ez a recept már létezik!')
      else toast.error('Hiba a mentéskor: ' + (detail || err?.message || 'ismeretlen hiba'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: any) => updateRecipe(id, data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['recipes'] })
      qc.invalidateQueries({ queryKey: ['recipe', vars.id] })
      setEditingRecipe(null)
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail || ''
      if (detail.startsWith('PLAN_LIMIT:')) toast.error(planLimitMessage(detail))
      else if (err?.response?.status === 409) toast.error('Ez a recept már létezik!')
      else toast.error('Hiba a mentéskor: ' + (detail || err?.message || 'ismeretlen hiba'))
    },
  })

  const [confirmDelete, setConfirmDelete] = useState<number | null>(null)

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteRecipe(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recipes'] })
      qc.invalidateQueries({ queryKey: ['subscription'] })
      setSelected(null)
      setConfirmDelete(null)
      toast.success('Recept törölve')
    },
    onError: () => toast.error('Hiba a törléskor'),
  })

  return (
    <div className="flex flex-col md:flex-row gap-4 md:h-full">
      {/* Bal: Lista — elrejtve ha valami ki van választva mobilon */}
      <div className={`md:w-72 md:shrink-0 ${(selected || (showForm && !editingRecipe)) ? 'hidden md:block' : ''}`}>
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold text-gray-900">Receptek</h1>
          <div className="flex items-center gap-2">
            <button className="text-sm px-2.5 py-1.5 border border-brand-300 text-brand-700 rounded-lg hover:bg-brand-50 transition-colors flex items-center gap-1.5"
              onClick={() => setShowAiImport(true)} title="AI recept feltöltés">
              <Sparkles size={14} />
            </button>
            <button className="btn-primary flex items-center gap-1.5 text-sm"
              onClick={() => {
                if (atLimit) { toast.error('Csomagkorlát elérve! Váltson magasabb csomagra.'); return }
                setShowForm(true); setEditingRecipe(null); setSelected(null)
              }}>
              {atLimit ? <Lock size={14} /> : <Plus size={14} />} Új recept
            </button>
          </div>
        </div>

        {showAiImport && (
          <AiImportModal
            active={!!sub?.recipe_ai_import_addon}
            onClose={() => setShowAiImport(false)}
            onImported={(draft) => {
              setAiDraft(draft)
              setShowAiImport(false)
              setShowForm(true)
              setEditingRecipe(null)
              setSelected(null)
            }}
          />
        )}

        <input
          type="text" className="input mb-3 text-sm" placeholder="Keresés..."
          value={recipeSearch} onChange={e => setRecipeSearch(e.target.value)}
        />

        <div className="card p-0">
          <div className="divide-y divide-gray-100">
            {(recipes as any[]).filter((r: any) => r.name.toLowerCase().includes(recipeSearch.toLowerCase())).map((r: any) => (
              <button key={r.id} onClick={() => { setSelected(r); setEditingRecipe(null); setDetailTab('recept') }}
                className={`w-full flex items-center justify-between px-3 py-3 text-left hover:bg-gray-50 transition-colors ${selected?.id === r.id ? 'bg-brand-50 border-l-2 border-brand-500' : ''}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 text-sm truncate flex items-center gap-1.5">
                    {r.locked && <Lock size={12} className="text-amber-500 shrink-0" />}
                    {r.name}
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                    {(r.ingredients?.length ?? 0) === 0 ? (
                      <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">nincs recept kitöltve</span>
                    ) : (
                      <span>{r.ingredients.length} alapanyag{r.serving_count ? ` · ${r.serving_count} ${r.yield_unit || 'db'}` : ''}</span>
                    )}
                    {r.shelf_life_days && (
                      <span className="flex items-center gap-0.5 text-brand-600">
                        <Clock size={10} />{r.shelf_life_days} nap
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight size={14} className="text-gray-400 shrink-0 ml-1" />
              </button>
            ))}
            {recipes.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">Még nincs recept</p>
            )}
          </div>
        </div>
      </div>

      {/* Jobb: Részletek */}
      {selected && (
        <div key={selected.id} className="flex-1 min-w-0 overflow-y-auto">
          <button className="md:hidden mb-3 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            onClick={() => { setSelected(null); setEditingRecipe(null) }}>
            <ChevronLeft size={16} /> Vissza a receptekhez
          </button>
          {recipeLoading ? (
            <div className="card flex items-center justify-center h-32 text-gray-400 text-sm">Betöltés...</div>
          ) : selectedRecipe && !editingRecipe ? (
            <div className="card">
              {/* Fejléc */}
              <div className="flex items-start gap-2 mb-3">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-bold text-gray-900 break-words">{selectedRecipe.name}</h2>
                  <div className="text-sm text-gray-500 flex items-center gap-3 mt-0.5">
                    {selectedRecipe.serving_count && (
                      <span><strong>{selectedRecipe.serving_count} {selectedRecipe.yield_unit || 'db'}</strong></span>
                    )}
                    {selectedRecipe.serving_weight_g && (
                      <span>1 db = <strong>{selectedRecipe.serving_weight_g} g</strong></span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {confirmDelete === selectedRecipe.id ? (
                    <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                      <span className="text-xs text-red-700 font-medium">Biztosan?</span>
                      <button className="text-xs bg-red-600 text-white px-2 py-0.5 rounded hover:bg-red-700"
                        onClick={() => deleteMutation.mutate(selectedRecipe.id)} disabled={deleteMutation.isPending}>
                        Igen
                      </button>
                      <button className="text-xs text-gray-500 hover:text-gray-700"
                        onClick={() => setConfirmDelete(null)}>Nem</button>
                    </div>
                  ) : (
                    <button className="p-1.5 rounded-lg border border-gray-200 text-red-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors"
                      onClick={() => setConfirmDelete(selectedRecipe.id)} title="Törlés">
                      <Trash2 size={14} />
                    </button>
                  )}
                  <button className="btn-secondary flex items-center gap-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => { setEditingRecipe(selectedRecipe); setShowForm(false) }}
                    disabled={selectedRecipe.locked}
                    title={selectedRecipe.locked ? 'Zárolva — előfizess a szerkesztéshez' : undefined}>
                    <Pencil size={13} /> Szerkesztés
                  </button>
                </div>
              </div>

              {selectedRecipe.locked && (
                <div className="flex items-start gap-2.5 p-3 mb-4 border border-amber-200 rounded-lg bg-amber-50 text-amber-800">
                  <Lock size={15} className="shrink-0 mt-0.5" />
                  <div className="text-sm">
                    Ez a recept a jelenlegi csomagod receptlimitje fölött van, ezért zárolva van: nem szerkeszthető és
                    nem generálható hozzá PDF. Fizess elő (vagy válts magasabb csomagra) a hozzáféréshez, vagy törölj
                    egy másik receptet, hogy visszaférj a limitbe.
                  </div>
                </div>
              )}

              {/* Tabok */}
              <div className="flex gap-1 border-b border-gray-200 mb-4 -mx-4 px-4">
                {(['recept', 'dokumentumok'] as const).map(tab => (
                  <button key={tab} onClick={() => setDetailTab(tab)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      detailTab === tab
                        ? 'border-brand-500 text-brand-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}>
                    {tab === 'recept' ? 'Recept' : 'Dokumentumok'}
                  </button>
                ))}
              </div>

              {/* Tab tartalom */}
              {detailTab === 'recept' && (
                <div>
                  {/* Alapanyagok szekciók szerint csoportosítva */}
                  {(() => {
                    const ings = selectedRecipe.ingredients
                    const sections: string[] = []
                    const grouped: Record<string, any[]> = {}
                    for (const ing of ings) {
                      const sec = ing.section || ''
                      if (!grouped[sec]) { grouped[sec] = []; sections.push(sec) }
                      grouped[sec].push(ing)
                    }
                    const uniqueSections = [...new Set(sections)]
                    const hasMultipleSections = uniqueSections.some(s => s !== '')
                    return uniqueSections.map(sec => (
                      <div key={sec} className="mb-3">
                        {hasMultipleSections && sec && (
                          <div className="text-xs font-semibold text-brand-700 uppercase mb-1 mt-2">{sec}</div>
                        )}
                        {!hasMultipleSections && (
                          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Alapanyagok</div>
                        )}
                        <div className="space-y-1">
                          {grouped[sec].map((ing: any) => (
                            <div key={ing.id} className="flex justify-between items-center text-sm py-1 border-b border-gray-50 last:border-0">
                              <div>
                                <span className="text-gray-800">{ing.ingredient_name}</span>
                                {ing.allergens?.length > 0 && (
                                  <span className="ml-1 text-xs text-amber-600 font-medium">⚠ {ing.allergens.join(', ')}</span>
                                )}
                              </div>
                              <span className="font-medium text-gray-700 ml-2 shrink-0">{ing.quantity ?? ing.quantity_grams} {ing.unit || 'g'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  })()}

                  {/* Allergén összefoglaló */}
                  <AllergenWarnings warnings={selectedRecipe.nutrition?.allergens || []} />

                  <div className="mt-3 space-y-1 text-sm text-gray-700">
                    {selectedRecipe.baking_temp && (
                      <div><span className="text-xs font-semibold text-gray-500 uppercase mr-1">Sütési hőfok / idő:</span>{selectedRecipe.baking_temp}</div>
                    )}
                    {(selectedRecipe.shelf_life_days || selectedRecipe.storage_temp) && (
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase mr-1">Minőségét megőrzi:</span>
                        {[selectedRecipe.shelf_life_days ? `${selectedRecipe.shelf_life_days} nap` : null, selectedRecipe.storage_temp].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {selectedRecipe.preservation_method && (
                      <div><span className="text-xs font-semibold text-gray-500 uppercase mr-1">Tartósítási eljárás:</span>{selectedRecipe.preservation_method}</div>
                    )}
                    {selectedRecipe.storage_notes && (
                      <div><span className="text-xs font-semibold text-gray-500 uppercase mr-1">Forgalmazás / tárolás:</span>{selectedRecipe.storage_notes}</div>
                    )}
                  </div>

                  {selectedRecipe.instructions && (
                    <div className="mt-4">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Leírás</div>
                      <p className="text-sm text-gray-600 whitespace-pre-line">{selectedRecipe.instructions}</p>
                    </div>
                  )}

                  {/* Tápérték — legalul */}
                  {selectedRecipe.nutrition ? (
                    <NutritionTable
                      nutrition={selectedRecipe.nutrition}
                      servingCount={selectedRecipe.serving_weight_g ? undefined : selectedRecipe.serving_count}
                      servingWeightG={selectedRecipe.serving_weight_g}
                      totalWeightG={selectedRecipe.batch_yield_grams}
                    />
                  ) : (
                    <div className="mt-4 text-xs text-gray-400 italic">
                      Tápérték nem számítható — hiányzó alapanyag adatok
                    </div>
                  )}
                </div>
              )}

              {detailTab === 'dokumentumok' && (
                <div>
                  <p className="text-xs text-gray-500 mb-3">
                    PDF dokumentumok generálása és letöltése
                  </p>
                  <PdfButtons recipe={selectedRecipe} />
                </div>
              )}
            </div>
          ) : editingRecipe ? (
            <div className="card">
              <div className="flex items-center gap-2 mb-4">
                <button className="md:hidden flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  onClick={() => setEditingRecipe(null)}>
                  <ChevronLeft size={16} /> Vissza
                </button>
                <h2 className="font-semibold">Recept szerkesztése: {editingRecipe.name}</h2>
              </div>
              <RecipeForm
                initial={editingRecipe}
                ingredients={ingredients}
                recipes={recipes}
                onSave={(data) => updateMutation.mutate({ id: editingRecipe.id, data })}
                onCancel={() => setEditingRecipe(null)}
              />
            </div>
          ) : null}
        </div>
      )}

      {showForm && !editingRecipe && (
        <div className="flex-1 min-w-0">
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <button className="md:hidden flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                onClick={() => setShowForm(false)}>
                <ChevronLeft size={16} /> Vissza
              </button>
              <h2 className="font-semibold">Új recept{aiDraft ? ' (AI javaslat alapján)' : ''}</h2>
            </div>
            <RecipeForm
              initial={aiDraft || undefined}
              ingredients={ingredients}
              recipes={recipes}
              onSave={(data) => createMutation.mutate(data)}
              onCancel={() => { setShowForm(false); setAiDraft(null) }}
            />
          </div>
        </div>
      )}

      {!selected && !showForm && (
        <div className="hidden md:flex flex-1 items-center justify-center text-gray-400 text-sm">
          Válasszon egy receptet a listából
        </div>
      )}
    </div>
  )
}
